import {
  APIWorkspaceFile,
  WorkspaceFileStatus,
  WorkspaceFileUploadResponse,
  WorkspaceFile,
} from '@/models/file';
import { API, hasBackend } from './api';
import { toast } from 'react-toastify';
import { parseWorkspaceFile } from '@/utils/files';
import {
  FolderTreeResponse,
  FileStatusList,
  StatusSummaryResponse,
  SyncRunListResponse,
} from '@/models/sharepoint';

export const LibraryService = {
  uploadFile: async (
    fileName: string,
    file: File,
    orgID?: string
  ): Promise<WorkspaceFileUploadResponse> => {
    try {
      if (!(file instanceof File)) {
        console.error('Invalid file object provided.');
        throw new Error('The provided file is not a valid File object.');
      }

      const { data: uploadResponseData } = (await API.post('/files/uploads', {
        file_name: fileName,
        content_type: file.type,
        organization_id: orgID,
      })) as {
        data: WorkspaceFileUploadResponse;
      };

      if (!uploadResponseData.presigned_url || !uploadResponseData.file_id) {
        console.error('Failed to get pre-signed URL or file ID for:', fileName);
        throw new Error(
          uploadResponseData.message ||
            `Failed to get pre-signed URL for "${fileName}".`
        );
      }

      const s3Headers = new Headers();
      Object.entries(uploadResponseData.headers).forEach(([key, value]) => {
        s3Headers.append(key, value);
      });

      const s3Response = await fetch(uploadResponseData.presigned_url, {
        method: 'PUT',
        headers: s3Headers,
        body: file,
      });

      if (!s3Response.ok) {
        console.error('Failed to upload file to S3 for:', fileName);
        throw new Error(`Failed to upload file "${fileName}" to S3.`);
      }

      return uploadResponseData;
    } catch (error) {
      console.error('Error during uploadFile process for:', fileName, error);
      throw new Error(
        (error as Error).message ||
          'An error occurred during the document upload process.'
      );
    }
  },

  deleteFile: async (fileID: string, orgID?: string): Promise<boolean> => {
    try {
      if (!hasBackend) {
        // Simulate deletion in mock mode
        toast.info(`File deleted (mock): ${fileID}`);
        return true;
      }
      const response = (await API.delete(`/files/${fileID}`, {
        params: {
          organization_id: orgID,
        },
      })) as {
        status: number;
      };

      if (response.status === 200) {
        toast.info('File deleted successfully.');
        return true;
      } else if (response.status === 403) {
        toast.error('You do not have permission to delete this file.');
        return false;
      } else if (response.status === 404) {
        toast.error('File not found.');
        return false;
      } else if (response.status === 409) {
        toast.error(
          'File is currently being uploaded or ingested and cannot be deleted.'
        );
        return false;
      } else if (response.status === 422) {
        toast.error('Validation error while deleting file.');
        return false;
      } else {
        toast.error('Failed to delete file.');
        return false;
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error(
        (error as Error).message || 'An error occurred while deleting the file.'
      );
      return false;
    }
  },
  getFile: async (
    fileID: string,
    orgID?: string
  ): Promise<WorkspaceFile | undefined> => {
    try {
      const response = (await API.get(`/files/${fileID}`, {
        params: {
          organization_id: orgID,
        },
      })) as {
        data: {
          status: string;
          file: APIWorkspaceFile;
        };
      };

      return parseWorkspaceFile(response.data.file);
    } catch (error) {
      return undefined;
    }
  },

  getFileStatus: async (
    fileID: string,
    orgID?: string
  ): Promise<{
    ready: boolean;
    uploadStatus: WorkspaceFileStatus;
    ingestStatus: WorkspaceFileStatus;
  }> => {
    const response = (await API.get(`/files/${fileID}/status`, {
      params: {
        organization_id: orgID,
      },
    })) as {
      data: {
        status: string;
        file_status: {
          mongodb_document_id: string;
          file_id: string;
          file_name: string;
          original_file_name: string;
          timestamp_ms: number;
          organization_id: string;
          user_id: string;
          source_type: string;
          upload_status: WorkspaceFileStatus;
          ingest_status: WorkspaceFileStatus;
          created_at: string;
          updated_at: string;
        };
      };
    };

    const { data } = response;

    return {
      ready:
        data.status === 'success' &&
        data.file_status.upload_status === 'COMPLETED' &&
        data.file_status.ingest_status === 'COMPLETED',
      uploadStatus: data.file_status.upload_status,
      ingestStatus: data.file_status.ingest_status,
    };
  },

  getFiles: async (
    orgID?: string,
    page: number = 1
  ): Promise<{ hasMoreFiles: boolean; files: WorkspaceFile[] }> => {
    const files: WorkspaceFile[] = [];

    if (!hasBackend) {
      return {
        hasMoreFiles: false,
        files,
      };
    }

    const skip = (page - 1) * 20; // Calculate the number of records to skip based on the page number
    const limit = 20; // Set the limit to 20 documents per page

    const response = (await API.get(`/files`, {
      params: {
        skip,
        limit,
        organization_id: orgID,
      },
    })) as {
      data: {
        status: string;
        total_count: number;
        files: Array<APIWorkspaceFile>;
      };
    };

    if (response.data.status !== 'success') {
      throw new Error('Failed to fetch files');
    }

    for (let i = 0; i < response.data.files.length; i++) {
      const file = response.data.files[i];

      files.push(parseWorkspaceFile(file));
    }

    return {
      hasMoreFiles: response.data.total_count > skip + limit,
      files,
    };
  },

  getAllFiles: async (orgID?: string): Promise<WorkspaceFile[]> => {
    const files: WorkspaceFile[] = [];

    if (!hasBackend) {
      return files;
    }

    let page = 1;
    let hasMoreFiles = true;

    while (hasMoreFiles) {
      const response = await LibraryService.getFiles(orgID, page);

      if (response.files.length > 0) {
        files.push(...response.files);
        page++;
      } else {
        hasMoreFiles = response.hasMoreFiles;
      }
    }

    return files;
  },
  getTotalFilesCount: async (orgID?: string): Promise<number> => {
    if (!hasBackend) return 0;

    const response = (await API.get(`/files`, {
      params: {
        skip: 0,
        limit: 1,
        organization_id: orgID,
      },
    })) as {
      data: {
        status: string;
        total_count: number;
        files: Array<APIWorkspaceFile>;
      };
    };

    if (response.data.status !== 'success') {
      throw new Error('Failed to fetch files');
    }

    return response.data.total_count;
  },
  getSharepointFolders: async (
    depth: number = 2,
    orgID?: string
  ): Promise<FolderTreeResponse> => {
    try {
      const response = (await API.get('/files/sharepoint/folders', {
        params: {
          depth,
          organization_id: orgID,
        },
      })) as { data: FolderTreeResponse };

      return response.data;
    } catch (error) {
      console.error('Error fetching SharePoint folder tree:', error);
      throw new Error((error as Error).message || 'Failed to fetch folders');
    }
  },

  getSharepointStatuses: async (opts?: {
    path_prefix?: string;
    ingest_status?: string;
    skip?: number;
    limit?: number;
    organization_id?: string;
  }): Promise<FileStatusList> => {
    try {
      const params: Record<string, unknown> = {};
      if (opts?.path_prefix) params.path_prefix = opts.path_prefix;
      if (opts?.ingest_status) params.ingest_status = opts.ingest_status;
      params.skip = opts?.skip ?? 0;
      params.limit = opts?.limit ?? 50;
      if (opts?.organization_id) params.organization_id = opts.organization_id;

      const response = (await API.get('/files/sharepoint/statuses', {
        params,
      })) as { data: FileStatusList };

      return response.data;
    } catch (error) {
      console.error('Error fetching SharePoint statuses:', error);
      throw new Error((error as Error).message || 'Failed to fetch statuses');
    }
  },

  getSharepointSummary: async (
    path_prefix?: string,
    orgID?: string
  ): Promise<StatusSummaryResponse> => {
    try {
      const params: Record<string, unknown> = {};
      if (path_prefix) params.path_prefix = path_prefix;
      if (orgID) params.organization_id = orgID;

      const response = (await API.get('/files/sharepoint/summary', {
        params,
      })) as { data: StatusSummaryResponse };

      return response.data;
    } catch (error) {
      console.error('Error fetching SharePoint summary:', error);
      throw new Error((error as Error).message || 'Failed to fetch summary');
    }
  },

  getSharepointSyncRuns: async (opts?: {
    drive_id?: string;
    status?: 'ok' | 'error' | string;
    skip?: number;
    limit?: number;
    organization_id?: string;
  }): Promise<SyncRunListResponse> => {
    try {
      const params: Record<string, unknown> = {};
      if (opts?.drive_id) params.drive_id = opts.drive_id;
      if (opts?.status) params.status = opts.status;
      params.skip = opts?.skip ?? 0;
      params.limit = opts?.limit ?? 20;
      if (opts?.organization_id) params.organization_id = opts.organization_id;

      const response = (await API.get('/files/sharepoint/sync-runs', {
        params,
      })) as { data: SyncRunListResponse };

      return response.data;
    } catch (error) {
      console.error('Error fetching SharePoint sync runs:', error);
      throw new Error((error as Error).message || 'Failed to fetch sync runs');
    }
  },
};

export default LibraryService;
